import qs from 'query-string';
import { createApi } from '@reduxjs/toolkit/query/react';
import { appEndpoint, orgEndpoint, workflowRunEndpoint } from '@/helpers/apiHelpers';
import type { TimelineEventsEntityRequest, TimelineEventsHistoryEntityRequest } from '@/actions/entities';
import type { TimelineEventsHistoryPaginatedResponse } from '@/actions/timelineEventsHistory';
import { type BaseAPIResponse, baseQuery } from './common';
import {
    AnyTimelineEventsFilter,
    AppFilter,
    AutomationFilter,
    DateFilter,
    DestinationFilter,
    ReleaseStepFilter,
    TimelineEventsFilterType,
    VersionFilter,
} from '@/types/timelineEventsFilters';

export interface OrgTimelineEventsAPIRequest {
    orgId: string;
    nextCursor?: string;
    timelineEventFilters?: AnyTimelineEventsFilter[];
    shouldShowAutomationsTimelineEventsOnly?: boolean;
    includeOrgTimelineFilters?: boolean;
}

export interface TimelineEventsAPIRequest extends TimelineEventsHistoryEntityRequest, TimelineEventsEntityRequest {
    timelineEventFilters?: AnyTimelineEventsFilter[];
    shouldShowAutomationsTimelineEventsOnly?: boolean;
    includeOrgTimelineFilters?: boolean;
}

export const paginationApi = createApi({
    baseQuery: baseQuery,
    reducerPath: 'paginationApi',
    tagTypes: ['TimelineEvents'],
    endpoints: (builder) => ({
        getTimelineEventsHistory: builder.query<
            BaseAPIResponse<TimelineEventsHistoryPaginatedResponse>,
            TimelineEventsAPIRequest
        >({
            query: ({
                orgId,
                appId,
                releaseId,
                releaseStepType,
                nextCursor,
                timelineEventFilters,
                shouldShowAutomationsTimelineEventsOnly,
            }) => {
                const encodedQueryParams = qs.stringify(
                    {
                        releaseId: releaseId ?? '',
                        releaseStepType: releaseStepType ?? '',
                        timelineEventsCursor: nextCursor ?? '',
                        ...timelineEventFiltersToQueryParams(
                            timelineEventFilters,
                            shouldShowAutomationsTimelineEventsOnly,
                            false,
                        ),
                    },
                    { skipNull: true, skipEmptyString: true },
                );

                const appUrl = appEndpoint(orgId, appId);
                return `${appUrl}/timelineEventsHistory?${encodedQueryParams}`;
            },
            providesTags: (_result, _error, { orgId, appId, releaseId, releaseStepType }) => [
                {
                    type: 'TimelineEvents',
                    id: `${orgId}-${appId}-${releaseId || 'all'}-${releaseStepType || 'all'}`,
                },
            ],
        }),
        getTimelineEvents: builder.query<
            BaseAPIResponse<TimelineEventsHistoryPaginatedResponse>,
            TimelineEventsAPIRequest
        >({
            query: ({
                orgId,
                workflowId,
                workflowRunId,
                stepRunIds,
                nextCursor,
                timelineEventFilters,
                shouldShowAutomationsTimelineEventsOnly,
            }) => {
                const encodedQueryParams = qs.stringify(
                    {
                        stepRunIds: stepRunIds.join(',') ?? [],
                        timelineEventsCursor: nextCursor ?? '',
                        ...timelineEventFiltersToQueryParams(
                            timelineEventFilters,
                            shouldShowAutomationsTimelineEventsOnly,
                            false,
                        ),
                    },
                    { skipNull: true, skipEmptyString: true },
                );

                const workflowUrl = workflowRunEndpoint({ orgId, workflowId, workflowRunId });
                return `${workflowUrl}/timelineEvents?${encodedQueryParams}`;
            },
            providesTags: (_result, _error, { orgId, workflowId, workflowRunId }) => [
                {
                    type: 'TimelineEvents',
                    id: `${orgId}-${workflowId}-${workflowRunId || 'all'}`,
                },
            ],
        }),
        getOrgTimelineEvents: builder.query<
            BaseAPIResponse<TimelineEventsHistoryPaginatedResponse>,
            OrgTimelineEventsAPIRequest
        >({
            query: ({
                orgId,
                nextCursor,
                timelineEventFilters,
                shouldShowAutomationsTimelineEventsOnly,
                includeOrgTimelineFilters,
            }) => {
                const encodedQueryParams = qs.stringify(
                    {
                        timelineEventsCursor: nextCursor ?? '',
                        ...timelineEventFiltersToQueryParams(
                            timelineEventFilters,
                            shouldShowAutomationsTimelineEventsOnly,
                            includeOrgTimelineFilters,
                        ),
                    },
                    { skipNull: true, skipEmptyString: true },
                );

                const orgUrl = orgEndpoint(orgId);
                return `${orgUrl}/timelineEvents?${encodedQueryParams}`;
            },
            providesTags: (_result, _error, { orgId }) => [
                {
                    type: 'TimelineEvents',
                    id: `${orgId}-all`,
                },
            ],
        }),
    }),
});

export const {
    useGetTimelineEventsHistoryQuery,
    useGetTimelineEventsQuery,
    useGetOrgTimelineEventsQuery,
    useLazyGetTimelineEventsHistoryQuery,
    useLazyGetTimelineEventsQuery,
    useLazyGetOrgTimelineEventsQuery,
} = paginationApi;

const timelineEventFiltersToQueryParams = (
    filters: AnyTimelineEventsFilter[] = [],
    shouldShowAutomationsTimelineEventsOnly = false,
    includeOrgTimelineFilters = false,
) => {
    const dateFilter = filters.find((filter) => filter.type === TimelineEventsFilterType.Date) as
        | DateFilter
        | undefined;
    const releaseSteps = filters
        .filter((filter) => filter.type === TimelineEventsFilterType.ReleaseStep)
        .map((filter) => (filter as ReleaseStepFilter).releaseStep);
    const automationTypes = filters
        .filter((filter) => filter.type === TimelineEventsFilterType.Automation)
        .map((filter) => (filter as AutomationFilter).automation);
    const versions = filters
        .filter((filter) => filter.type === TimelineEventsFilterType.Version)
        .map((filter) => (filter as VersionFilter).version);
    const appIds = filters
        .filter((filter) => filter.type === TimelineEventsFilterType.App)
        .map((filter) => (filter as AppFilter).appId);
    const destinationStepConfigIds = filters
        .filter((filter) => filter.type === TimelineEventsFilterType.Destination)
        .map((filter) => (filter as DestinationFilter).destinationStepConfigId);

    return {
        startDate: dateFilter?.date?.[0] ? timelineEventDateToString(dateFilter.date[0]) : undefined,
        endDate: dateFilter?.date?.[1] ? timelineEventDateToString(dateFilter.date[1]) : undefined,
        releaseSteps: releaseSteps.length ? releaseSteps.join(',') : undefined,
        automationTypes: automationTypes.length ? automationTypes.join(',') : undefined,
        versions: versions.length ? versions.join(',') : undefined,
        appIds: includeOrgTimelineFilters && appIds.length ? appIds.join(',') : undefined,
        destinationStepConfigIds:
            includeOrgTimelineFilters && destinationStepConfigIds.length ?
                destinationStepConfigIds.join(',')
            :   undefined,
        automationsOnly: shouldShowAutomationsTimelineEventsOnly || undefined,
    };
};

const timelineEventDateToString = (date: unknown): string => {
    if (typeof date === 'string') {
        return date;
    }

    if (date && typeof (date as { format?: (format: string) => string }).format === 'function') {
        return (date as { format: (format: string) => string }).format('YYYY-MM-DD');
    }

    if (date && typeof (date as { toISOString?: () => string }).toISOString === 'function') {
        return (date as { toISOString: () => string }).toISOString();
    }

    return String(date);
};
